import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

/**
 * ProtectedRoute
 *
 * Supports three gating modes:
 *   1. Unauthenticated → bounces to /login (preserving the originally
 *      requested path in location.state.from for deep-link return).
 *   2. `roles=["admin","manager"]` → legacy single-role gate (kept for
 *      pre-Sprint-9 pages).
 *   3. `perm="order.confirm"` or `perm={["order.confirm","order.approve"]}`
 *      → permission-based gate using the resolved permissions[] from the
 *      backend. Backend remains authoritative.
 *
 * Clients that fail the gate are redirected to /app/dashboard (the
 * Sprint 10 authenticated home) rather than shown a 403, because the
 * fallback happens before the user even sees the page. Server-side
 * protection still returns 403 on any API misuse.
 */
export default function ProtectedRoute({ children, roles, perm }) {
  const { user, initializing, hasPermission } = useAuth();
  const location = useLocation();

  if (initializing) {
    return (
      <div
        data-testid="auth-initializing"
        className="min-h-screen flex items-center justify-center bg-[var(--phx-brand-canvas)]"
      >
        <div className="text-xs uppercase tracking-[var(--phx-tracking)] text-slate-500">
          Verifying session…
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  if (perm && !hasPermission(perm)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
}
